import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {TextInput, View} from 'react-native'
import {
  type AppBskyActorDefs,
  moderateProfile,
  type ModerationOpts,
} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'

import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {isWeb} from '#/platform/detection'
import {useCheckMatrixUsers} from '#/state/matrix/hooks/useMatrixUsers'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {STALE} from '#/state/queries'
import {useActorAutocompleteQuery} from '#/state/queries/actor-autocomplete'
import {useBateskyMembersQuery} from '#/state/queries/batesky-members'
import {useAgent, useSession} from '#/state/session'
import {type ListMethods} from '#/view/com/util/List'
import {android, atoms as a, native, useTheme, web} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {canBeMessaged} from '#/components/dms/util'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {MagnifyingGlass2_Stroke2_Corner0_Rounded as Search} from '#/components/icons/MagnifyingGlass2'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import * as ProfileCard from '#/components/ProfileCard'
import {Text} from '#/components/Typography'
import type * as bsky from '#/types/bsky'

type ProfileItem = {
  type: 'profile'
  key: string
  profile: bsky.profile.AnyProfileView
}

type EmptyItem = {
  type: 'empty'
  key: string
  message: string
}

type PlaceholderItem = {
  type: 'placeholder'
  key: string
}

type Item = ProfileItem | EmptyItem | PlaceholderItem

/**
 * Batch-resolve Bluesky profiles for a list of DIDs.
 * getProfiles supports max 25 actors per call.
 */
function useMemberProfilesQuery(dids: string[]) {
  const agent = useAgent()

  return useQuery<AppBskyActorDefs.ProfileViewDetailed[]>({
    staleTime: STALE.MINUTES.FIVE,
    queryKey: ['batesky-member-profiles', dids],
    async queryFn() {
      if (dids.length === 0) return []

      const profiles: AppBskyActorDefs.ProfileViewDetailed[] = []
      // Batch in chunks of 25 (AT Protocol limit)
      for (let i = 0; i < dids.length; i += 25) {
        const chunk = dids.slice(i, i + 25)
        const res = await agent.getProfiles({actors: chunk})
        profiles.push(...res.data.profiles)
      }
      return profiles
    },
    enabled: dids.length > 0,
  })
}

export function SecureSearchablePeopleList({
  title,
  onSelectChat,
}: {
  title: string
  onSelectChat: (did: string) => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  const moderationOpts = useModerationOpts()
  const control = Dialog.useDialogContext()
  const [headerHeight, setHeaderHeight] = useState(0)
  const listRef = useRef<ListMethods>(null)
  const {currentAccount} = useSession()
  const inputRef = useRef<TextInput>(null)
  const [searchText, setSearchText] = useState('')

  const {registeredDids, checkUsers} = useCheckMatrixUsers()

  // Fetch all Batesky members
  const {data: members} = useBateskyMembersQuery()

  // Resolve Bluesky profiles for all members
  const memberDids = useMemo(() => (members ?? []).map(m => m.did), [members])
  const {data: memberProfiles} = useMemberProfilesQuery(memberDids)

  // Check Matrix registration for members
  useEffect(() => {
    if (memberDids.length) checkUsers(memberDids)
  }, [memberDids, checkUsers])

  // Search typeahead for when user types
  const {
    data: searchResults,
    isError,
    isFetching: isSearching,
  } = useActorAutocompleteQuery(searchText, true, 12)

  // Check Matrix registration for search results
  useEffect(() => {
    if (searchResults?.length) {
      const dids = searchResults
        .filter(p => p.did !== currentAccount?.did)
        .map(p => p.did)
      if (dids.length) checkUsers(dids)
    }
  }, [searchResults, currentAccount?.did, checkUsers])

  const items = useMemo(() => {
    const _items: Item[] = []

    if (isError) {
      _items.push({
        type: 'empty',
        key: 'empty',
        message: _(msg`We're having network issues, try again`),
      })
    } else if (searchText.length) {
      // Search mode: show any Matrix-registered user who can be messaged
      if (searchResults?.length) {
        for (const profile of searchResults) {
          if (profile.did === currentAccount?.did) continue
          if (!registeredDids.has(profile.did)) continue
          if (!canBeMessaged(profile)) continue
          _items.push({
            type: 'profile',
            key: profile.did,
            profile,
          })
        }
      }
    } else {
      // Default list: all Batesky members who are on Matrix and can be messaged
      if (memberProfiles) {
        for (const profile of memberProfiles) {
          if (profile.did === currentAccount?.did) continue
          if (!registeredDids.has(profile.did)) continue
          if (!canBeMessaged(profile)) continue
          _items.push({
            type: 'profile',
            key: profile.did,
            profile,
          })
        }
      } else {
        // Loading state
        const placeholders: Item[] = Array(10)
          .fill(0)
          .map((__, i) => ({
            type: 'placeholder' as const,
            key: i + '',
          }))
        _items.push(...placeholders)
      }
    }

    return _items
  }, [
    _,
    searchText,
    searchResults,
    isError,
    currentAccount?.did,
    memberProfiles,
    registeredDids,
  ])

  if (searchText && !isSearching && !items.length && !isError) {
    items.push({
      type: 'empty',
      key: 'empty',
      message: _(msg`No secure chat users found`),
    })
  }

  const renderItems = useCallback(
    ({item}: {item: Item}) => {
      switch (item.type) {
        case 'profile': {
          return (
            <SecureProfileCard
              key={item.key}
              profile={item.profile}
              moderationOpts={moderationOpts!}
              onPress={onSelectChat}
            />
          )
        }
        case 'placeholder': {
          return <ProfileCardSkeleton key={item.key} />
        }
        case 'empty': {
          return <Empty key={item.key} message={item.message} />
        }
        default:
          return null
      }
    },
    [moderationOpts, onSelectChat],
  )

  useLayoutEffect(() => {
    if (isWeb) {
      setImmediate(() => {
        inputRef?.current?.focus()
      })
    }
  }, [])

  const listHeader = useMemo(() => {
    return (
      <View
        onLayout={evt => setHeaderHeight(evt.nativeEvent.layout.height)}
        style={[
          a.relative,
          web(a.pt_lg),
          native(a.pt_4xl),
          android({
            borderTopLeftRadius: a.rounded_md.borderRadius,
            borderTopRightRadius: a.rounded_md.borderRadius,
          }),
          a.pb_xs,
          a.px_lg,
          a.border_b,
          t.atoms.border_contrast_low,
          t.atoms.bg,
        ]}>
        <View style={[a.relative, native(a.align_center), a.justify_center]}>
          <Text
            style={[
              a.z_10,
              a.text_lg,
              a.font_heavy,
              a.leading_tight,
              t.atoms.text_contrast_high,
            ]}>
            {title}
          </Text>
          {isWeb ? (
            <Button
              label={_(msg`Close`)}
              size="small"
              shape="round"
              variant="ghost"
              color="secondary"
              style={[
                a.absolute,
                a.z_20,
                web({right: -4}),
                native({right: 0}),
                native({height: 32, width: 32, borderRadius: 16}),
              ]}
              onPress={() => control.close()}>
              <ButtonIcon icon={X} size="md" />
            </Button>
          ) : null}
        </View>

        <View style={web([a.pt_xs])}>
          <SearchInput
            inputRef={inputRef}
            value={searchText}
            onChangeText={text => {
              setSearchText(text)
              listRef.current?.scrollToOffset({offset: 0, animated: false})
            }}
            onEscape={control.close}
          />
        </View>
      </View>
    )
  }, [
    t.atoms.border_contrast_low,
    t.atoms.bg,
    t.atoms.text_contrast_high,
    _,
    title,
    searchText,
    control,
  ])

  return (
    <Dialog.InnerFlatList
      ref={listRef}
      data={items}
      renderItem={renderItems}
      ListHeaderComponent={listHeader}
      stickyHeaderIndices={[0]}
      keyExtractor={(item: Item) => item.key}
      style={[
        web([a.py_0, {height: '100vh', maxHeight: 600}, a.px_0]),
        native({height: '100%'}),
      ]}
      webInnerContentContainerStyle={a.py_0}
      webInnerStyle={[a.py_0, {maxWidth: 500, minWidth: 200}]}
      scrollIndicatorInsets={{top: headerHeight}}
      keyboardDismissMode="on-drag"
    />
  )
}

function SecureProfileCard({
  profile,
  moderationOpts,
  onPress,
}: {
  profile: bsky.profile.AnyProfileView
  moderationOpts: ModerationOpts
  onPress: (did: string) => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  const moderation = moderateProfile(profile, moderationOpts)
  const displayName = sanitizeDisplayName(
    profile.displayName || sanitizeHandle(profile.handle),
    moderation.ui('displayName'),
  )

  const handleOnPress = useCallback(() => {
    onPress(profile.did)
  }, [onPress, profile.did])

  return (
    <Button
      label={_(msg`Start secure chat with ${displayName}`)}
      onPress={handleOnPress}>
      {({hovered, pressed, focused}) => (
        <View
          style={[
            a.flex_1,
            a.py_sm,
            a.px_lg,
            pressed || focused || hovered ? t.atoms.bg_contrast_25 : t.atoms.bg,
          ]}>
          <ProfileCard.Header>
            <ProfileCard.Avatar
              profile={profile}
              moderationOpts={moderationOpts}
            />
            <ProfileCard.NameAndHandle
              profile={profile}
              moderationOpts={moderationOpts}
            />
          </ProfileCard.Header>
        </View>
      )}
    </Button>
  )
}

function SearchInput({
  inputRef,
  value,
  onChangeText,
  onEscape,
}: {
  inputRef: React.RefObject<TextInput>
  value: string
  onChangeText: (text: string) => void
  onEscape: () => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  const {
    state: hovered,
    onIn: onMouseEnter,
    onOut: onMouseLeave,
  } = useInteractionState()
  const {state: focused, onIn: onFocus, onOut: onBlur} = useInteractionState()
  const interacted = hovered || focused

  return (
    <View
      {...web({onMouseEnter, onMouseLeave})}
      style={[
        a.flex_row,
        a.align_center,
        a.gap_sm,
        a.px_md,
        a.py_sm,
        a.mb_sm,
        a.rounded_md,
        t.atoms.bg_contrast_25,
        interacted && {
          backgroundColor: t.atoms.bg_contrast_50.backgroundColor,
        },
      ]}>
      <Search
        size="md"
        fill={
          interacted
            ? t.atoms.text_contrast_high.color
            : t.atoms.text_contrast_medium.color
        }
      />
      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={onChangeText}
        onFocus={onFocus}
        onBlur={onBlur}
        returnKeyType="search"
        placeholder={_(msg`Search`)}
        placeholderTextColor={t.atoms.text_contrast_medium.color}
        accessibilityLabel={_(msg`Search for users`)}
        accessibilityHint={_(
          msg`Search for Batesky users to start a secure chat`,
        )}
        style={[
          a.flex_1,
          a.text_md,
          t.atoms.text,
          {lineHeight: a.text_md.fontSize * 1.1875},
        ]}
        onKeyPress={e => {
          if ('key' in e.nativeEvent && e.nativeEvent.key === 'Escape') {
            onEscape()
          }
        }}
        autoCorrect={false}
        autoComplete="off"
        autoCapitalize="none"
      />
    </View>
  )
}

function ProfileCardSkeleton() {
  const t = useTheme()
  return (
    <View
      style={[
        a.flex_1,
        a.py_sm,
        a.px_lg,
        a.gap_md,
        a.flex_row,
        a.align_center,
      ]}>
      <View
        style={[
          {width: 42, height: 42},
          a.rounded_full,
          t.atoms.bg_contrast_50,
        ]}
      />
      <View style={[a.gap_xs]}>
        <View
          style={[
            {width: 120, height: 14},
            a.rounded_xs,
            t.atoms.bg_contrast_50,
          ]}
        />
        <View
          style={[
            {width: 80, height: 10},
            a.rounded_xs,
            t.atoms.bg_contrast_25,
          ]}
        />
      </View>
    </View>
  )
}

function Empty({message}: {message: string}) {
  const t = useTheme()
  return (
    <View style={[a.p_lg, a.align_center]}>
      <Text style={[t.atoms.text_contrast_medium]}>{message}</Text>
    </View>
  )
}
